/**
 * Platform Admin Resource & Task Control V2 — 任务控制中心 (spec §十六-
 * §七十二/§八十九-§九十二). Upgrades the old read-only "最近任务" table into
 * a real server-side filtered/paginated/sorted task management surface:
 * date/status/type/worker/search filters, a generic Task Detail Drawer,
 * single + batch admin delete, and real manual queue reordering (drag
 * handle) that changes the actual Worker claim order — never only a
 * front-end `array.sort()` (spec §五十八).
 *
 * Queue-reorder scoping (documented honestly, see
 * docs/platform/PLATFORM_TASK_CONTROL.md): `JobService.reorder_queue`'s
 * `new_index` is a 0-based position within the *entire* `queued` set,
 * not within whatever filtered/paginated slice happens to be on screen.
 * Dragging is therefore only ever enabled in the dedicated "队列顺序" view
 * with every other filter cleared and `status=queued` forced, and only
 * while the whole queue fits on one page (`page_size` forced to the
 * server's own 200 max) — in that one state, the visible on-screen order
 * is provably identical to the real global queue order, so a drop
 * position maps 1:1 onto a correct `new_index`. Any other filter/sort
 * combination hides the drag handles entirely (spec §七十一/§七十二: "切换
 * 至'队列顺序'后可调整任务优先级").
 */
import {
  CaretRightOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  HolderOutlined,
  PauseOutlined,
  ProfileOutlined,
  RedoOutlined,
  ReloadOutlined,
  SearchOutlined,
  VerticalAlignTopOutlined,
} from '@ant-design/icons'
import type { TableColumnsType, TableProps } from 'antd'
import { Button, DatePicker, Drawer, Input, Modal, Select, Table, Tag, Tooltip, message } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  platformBatchDeleteTasks,
  platformBatchPauseTasks,
  platformBatchResumeTasks,
  platformDeleteTask,
  platformGetTaskDetail,
  platformGetTaskFilterOptions,
  platformListTasks,
  platformPauseTask,
  platformPinTask,
  platformRedoTask,
  platformReorderTask,
  platformResumeTask,
  platformSetWorkerDesiredState,
  platformTaskMediaDownloadUrl,
} from '../api'
import type { PlatformTaskFilterOptionsResponse } from '../api'
import type {
  AdminWorkerRow,
  PlatformTaskDetail,
  PlatformTaskExecutionLog,
  PlatformTaskExecutionLogEvent,
  PlatformTaskListResponse,
  PlatformTaskRow,
  TaskDiagnosis,
  TaskDiagnosticAction,
} from '../types'
import { VistoeraActionConfirm } from '../ui/action'
import { SectionCard, StatusBadge, formatDateTime } from './adminShared'
import TaskResultModal from './TaskResultModal'
import { platformColors } from './theme'
import {
  DEFAULT_TASK_CONTROL_CUSTOM_SORT,
  resolveTaskControlListSort,
  type TaskControlCustomSort,
  type TaskControlViewMode,
} from './taskControlListSort'

const { RangePicker } = DatePicker

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'queued', label: '排队中' },
  { value: 'paused', label: '已暂停' },
  { value: 'running', label: '运行中' },
  { value: 'waiting_for_review', label: '待审核' },
  { value: 'succeeded', label: '成功' },
  { value: 'failed', label: '失败' },
  { value: 'cancelled', label: '已取消' },
  { value: 'interrupted', label: '已中断' },
]

/** VistoeraTASK-00362 — the "全部类型"/"全部执行方" dropdown options used to
 * be this one hardcoded 4-entry array (missing most real business types —
 * 文本生成/生成创意/生成剧本/生成分镡/角色生成/...) plus a Worker-Registry-
 * only executor list (missing every API Provider executor, e.g. Volcengine
 * Ark · Seedream). Both are now fetched from the backend's canonical
 * `GET /platform/tasks/filter-options` (see `platformGetTaskFilterOptions`)
 * so a future business type/Provider/Worker never requires a frontend
 * redeploy — see `EMPTY_FILTER_OPTIONS` below for the loading-state
 * fallback (never an empty "全部类型" dropdown while the request is in
 * flight; just the "全部" sentinel option until it resolves). */
const EMPTY_FILTER_OPTIONS: PlatformTaskFilterOptionsResponse = { task_types: [], api_executors: [] }

type ViewMode = TaskControlViewMode

interface Filters {
  status: string
  /** Canonical BUSINESS_TASK_TYPE id (task spec: TYPE != EXECUTOR != RAW_JOB_TYPE != PROVIDER). */
  businessType: string
  /** Canonical executor id: `worker:<worker_id>` or `api:<provider>:<model>`. */
  executor: string
  search: string
  dateFrom: string | null
  dateTo: string | null
}

const EMPTY_FILTERS: Filters = { status: '', businessType: '', executor: '', search: '', dateFrom: null, dateTo: null }

function copyToClipboard(text: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => message.success('已复制'))
    .catch(() => message.error('复制失败'))
}

function TaskIdCell({ taskId }: { taskId: string }) {
  return (
    <Tooltip title={`${taskId}（点击复制）`}>
      <span
        style={{ fontFamily: 'monospace', cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation()
          copyToClipboard(taskId)
        }}
      >
        {taskId.slice(0, 8)}
      </span>
    </Tooltip>
  )
}

/** Tenant = the task's Workspace (owner email). When a team member submitted
 * the task, the creator is shown first so a member's task never reads as
 * "someone else's". */
function AdminTestBadge({ row }: { row: PlatformTaskRow }) {
  if (!row.is_admin_test) return null
  return (
    <Tooltip
      title={`Admin Direct Test · 目标 Worker：${row.admin_test_target_worker_id ?? '—'} · 发起人：${row.admin_test_requested_by_email ?? '—'}`}
    >
      <Tag color="purple" style={{ marginInlineEnd: 0, fontSize: 11 }}>
        ADMIN TEST
      </Tag>
    </Tooltip>
  )
}

function TenantCell({ row }: { row: PlatformTaskRow }) {
  const { t } = useTranslation('platformAdmin')
  if (row.is_admin_test) return <AdminTestBadge row={row} />
  const owner = row.tenant_account ?? row.tenant_name
  const creator = row.created_by_email ?? null
  const tip = [
    row.tenant_name ? `Workspace：${row.tenant_name}` : null,
    owner ? `Workspace Owner：${owner}` : null,
    creator ? `创建者：${creator}` : '创建者：未记录',
    row.tenant_id ? `Workspace ID：${row.tenant_id}` : null,
  ]
    .filter(Boolean)
    .join('\n')
  if (!owner && !creator) return <>—</>
  const anomaly = row.created_by_is_member === false
  if (!creator) {
    // No recorded creator: never present the Workspace Owner as the creator.
    return (
      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
        <div style={{ lineHeight: 1.35 }}>
          <div style={{ color: platformColors.mutedText, fontStyle: 'italic' }}>
            {t('platformAdmin:taskControl.creatorUnrecorded')}
          </div>
          <div style={{ color: platformColors.mutedText, fontSize: 11 }}>Owner：{owner}</div>
        </div>
      </Tooltip>
    )
  }
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
      <div style={{ lineHeight: 1.35 }}>
        <div style={anomaly ? { color: platformColors.danger } : undefined}>{creator}</div>
        {creator && owner && creator !== owner ? (
          <div style={{ color: platformColors.mutedText, fontSize: 11 }}>团队：{row.tenant_name ?? owner}</div>
        ) : null}
        {anomaly ? <div style={{ color: platformColors.danger, fontSize: 11 }}>创建者非该 Workspace 成员</div> : null}
      </div>
    </Tooltip>
  )
}

function WorkerCell({
  worker,
  row,
}: {
  worker: PlatformTaskRow['worker']
  row?: PlatformTaskRow
}) {
  const display = worker.display || '—'
  const isWaiting = display === '等待分配'
  // VistoeraTASK-00675 — Direct Test: while waiting, surface the pinned target
  // so Admin does not confuse "unassigned" with "PC3 will take it".
  if (isWaiting && row?.is_admin_test && row.admin_test_target_worker_id) {
    return (
      <Tooltip title={`Admin Direct Test 目标：${row.admin_test_target_worker_id}`}>
        <span style={{ color: platformColors.mutedText }}>等待 {row.admin_test_target_worker_id}</span>
      </Tooltip>
    )
  }
  if (isWaiting) {
    return (
      <span style={{ color: platformColors.mutedText, fontSize: 12, fontStyle: 'italic' }}>{display}</span>
    )
  }
  if (worker.worker_online === false) {
    return <span style={{ color: platformColors.danger, fontSize: 12 }}>{display}</span>
  }
  return <span style={{ fontSize: 12 }}>{display}</span>
}

const STAGE_LABEL: Record<string, string> = {
  HOST: 'Host',
  WORKER_PROCESS: 'Worker Process',
  SCHEDULING: 'Scheduling（调度）',
  CAPABILITY: 'Capability（能力匹配）',
  CLAIM: 'Claim（领取）',
  EXECUTION: 'Execution（执行）',
  RESULT: 'Result（结果提交）',
  ADMIN: 'Admin（管理员操作）',
  UNKNOWN: '未知',
}

const RETRYABLE_LABEL: Record<string, string> = {
  YES: '可直接重试',
  NO: '请先修复配置后再重试',
  UNKNOWN: '重试效果未知，建议先查看详情',
}

/** VISTOERA_TASK_FAILURE_DIAGNOSTICS_WORKER_ELIGIBILITY_AND_ADMIN_
 * RECOVERY_GUIDANCE_V1 (spec §15) — 失败时 vs 现在两套独立状态，从不合并
 * 展示；`online: undefined` 的 failure-time 快照永远只显示已知字段。 */
function WorkerStateSummary({ state }: { state: TaskDiagnosis['worker_state_at_failure'] }) {
  if (!state) return <span style={{ color: platformColors.mutedText }}>—</span>
  const parts: string[] = []
  if (state.host_online === false) parts.push('主机不可达')
  else if (state.host_online === true) parts.push('主机在线')
  if (state.process_online === false) parts.push('进程未运行')
  else if (state.process_online === true) parts.push('进程运行中')
  if (state.desired_state) parts.push(`期望状态=${state.desired_state}`)
  if (state.accepting_jobs === false) parts.push('未接单')
  if (state.online === false) parts.push('离线')
  return <span>{state.worker_name || state.worker_id}：{parts.join(' · ') || '—'}</span>
}

/** Spec §12/§13 — the Diagnostic Drawer's admin-facing body: first broken
 * stage / canonical reason / evidence / failure-time vs current Worker
 * state / retryability / recommended actions. Never rendered for a
 * non-`failed` task (`diagnosis` is `null` — see backend `diagnose_job`). */
function TaskDiagnosisSection({
  diagnosis,
  onAction,
  actionBusy,
}: {
  diagnosis: TaskDiagnosis
  onAction: (action: TaskDiagnosticAction) => void
  actionBusy: string | null
}) {
  return (
    <>
      <DetailSectionTitle>任务诊断</DetailSectionTitle>
      <DetailRow label="失败阶段" value={STAGE_LABEL[diagnosis.first_broken_stage] ?? diagnosis.first_broken_stage} />
      <DetailRow label="原因" value={<span style={{ fontWeight: 600 }}>{diagnosis.admin_reason}</span>} />
      <DetailRow label="详情" value={diagnosis.admin_detail} />
      <DetailRow label="是否已领取" value={diagnosis.evidence.claim_occurred ? '是 — Worker 已领取任务' : '否 — 任务从未被任何 Worker 领取'} />
      <DetailRow label="可重试性" value={RETRYABLE_LABEL[diagnosis.retryable] ?? diagnosis.retryable} />
      <DetailRow label="失败时 Worker 状态" value={<WorkerStateSummary state={diagnosis.worker_state_at_failure} />} />
      <DetailRow
        label="当前 Worker 状态"
        value={
          <>
            <WorkerStateSummary state={diagnosis.worker_state_now} />
            {diagnosis.worker_recovered_since_failure && (
              <div style={{ color: platformColors.success, marginTop: 4 }}>该 Worker 当前已恢复在线</div>
            )}
          </>
        }
      />
      {!diagnosis.snapshot_available && (
        <div style={{ fontSize: 12, color: platformColors.mutedText, marginTop: 4 }}>
          该任务失败于诊断快照功能上线之前，以上「失败时」状态为不可得（仅显示当前状态推断，可能与失败当时不完全一致）。
        </div>
      )}
      {diagnosis.eligibility && diagnosis.eligibility.ineligible_workers.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: platformColors.secondaryText, marginBottom: 4 }}>
            候选 Worker（{diagnosis.eligibility.capability_label ?? diagnosis.eligibility.capability_id}）：
          </div>
          {diagnosis.eligibility.ineligible_workers.map((w) => (
            <div key={w.worker_id} style={{ fontSize: 12, color: platformColors.mutedText }}>
              {w.worker_name ?? w.worker_id} — {w.reason_code ?? '未知原因'}
            </div>
          ))}
        </div>
      )}
      {diagnosis.recommended_actions.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {diagnosis.recommended_actions.map((action) => (
            <Button key={action.kind} size="small" type="primary" loading={actionBusy === action.kind} onClick={() => onAction(action)}>
              {action.label}
            </Button>
          ))}
        </div>
      )}
      <details style={{ marginTop: 12, fontSize: 12, color: platformColors.mutedText }}>
        <summary style={{ cursor: 'pointer' }}>技术详情</summary>
        <div style={{ marginTop: 6 }}>
          <div>Diagnostic Code: {diagnosis.canonical_code}</div>
          <div>Raw Error Code: {diagnosis.evidence.raw_error_code ?? '—'}</div>
          <div>Raw Error Detail: {diagnosis.evidence.raw_error_detail ?? '—'}</div>
          <div>Stage: {diagnosis.evidence.stage ?? '—'}</div>
          <div>Attempt: {diagnosis.evidence.attempt_count ?? '—'}</div>
        </div>
      </details>
    </>
  )
}

/** VISTOERA_TASK_FAILURE_DIAGNOSTICS_WORKER_ELIGIBILITY_AND_ADMIN_
 * RECOVERY_GUIDANCE_V1 (spec §10/§11) — the list-row-visible diagnosis:
 * a failed task's 状态 cell must never read as a bare "失败" the admin
 * has to open a drawer just to categorize (spec §11: "避免管理员必须进入
 * 详情才能知道大分类"). Purely a presentational read of the already-loaded
 * `diagnostic_summary` (spec §27 — zero extra requests per row). */
function DiagnosticSummaryLine({ row }: { row: PlatformTaskRow }) {
  const summary = row.diagnostic_summary
  if (row.status !== 'failed' || !summary) return null
  return (
    <div style={{ fontSize: 11, color: platformColors.danger, marginTop: 2 }}>
      {summary.label}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', padding: '8px 0', borderBottom: `1px solid ${platformColors.border}`, fontSize: 13 }}>
      <div style={{ width: 96, color: platformColors.secondaryText, flexShrink: 0 }}>{label}</div>
      <div style={{ color: platformColors.primaryText, wordBreak: 'break-word', flex: 1 }}>{value}</div>
    </div>
  )
}

function DetailSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 600, color: platformColors.primaryText, marginTop: 20, marginBottom: 4 }}>
      {children}
    </div>
  )
}

/** Spec §三十五-§三十七 — generic Task Detail shell (metadata / params /
 * input / output / timeline / error-runtime), with a `job_type`-specific
 * params renderer plugged in rather than a whole separate page per type. */
function TaskParamsRenderer({ detail }: { detail: PlatformTaskDetail }) {
  const payload = detail.payload || {}
  if (detail.job_type === 'video_generation') {
    return (
      <>
        <DetailRow label="提示词" value={(payload.prompt as string) || '—'} />
        {payload.enhanced_prompt != null && <DetailRow label="增强提示词" value={String(payload.enhanced_prompt)} />}
        {payload.reference_image_asset_id != null && <DetailRow label="参考图片" value={String(payload.reference_image_asset_id)} />}
        {payload.reference_video_asset_id != null && <DetailRow label="参考视频" value={String(payload.reference_video_asset_id)} />}
        {payload.duration_seconds != null && <DetailRow label="视频时长" value={`${payload.duration_seconds}s`} />}
        {payload.resolution != null && <DetailRow label="分辨率" value={String(payload.resolution)} />}
        {payload.aspect_ratio != null && <DetailRow label="宽高比" value={String(payload.aspect_ratio)} />}
        {payload.count != null && <DetailRow label="数量" value={String(payload.count)} />}
        {payload.seed != null && <DetailRow label="Seed" value={String(payload.seed)} />}
      </>
    )
  }
  if (detail.job_type === 'run_chain_pipeline' || detail.job_type === 'generate_saas_deepzoom') {
    return (
      <>
        {payload.asset_id != null && <DetailRow label="Asset ID" value={String(payload.asset_id)} />}
        {payload.related_asset_id != null && <DetailRow label="关联 Asset" value={String(payload.related_asset_id)} />}
        {detail.site_id != null && <DetailRow label="Site ID" value={detail.site_id} />}
      </>
    )
  }
  return null
}

function RawJsonBlock({ label, value }: { label: string; value: Record<string, unknown> }) {
  if (!value || Object.keys(value).length === 0) return null
  return (
    <>
      <DetailSectionTitle>{label}</DetailSectionTitle>
      <pre
        style={{
          background: '#F8FAFC',
          border: `1px solid ${platformColors.border}`,
          borderRadius: 6,
          padding: 10,
          fontSize: 12,
          maxHeight: 200,
          overflow: 'auto',
          margin: 0,
        }}
      >
        {JSON.stringify(value, null, 2)}
      </pre>
    </>
  )
}

/** VistoeraTASK-00413 — 任务执行日志 V1. One row in the readable event
 * timeline (spec §二: timestamp / level / message, plus stage and, for a
 * failure event, Error Code/Detail inline). */
function ExecutionEventRow({ event }: { event: PlatformTaskExecutionLogEvent }) {
  const levelColor =
    event.level === 'ERROR'
      ? platformColors.danger
      : event.level === 'WARNING'
        ? '#D97706'
        : event.level === 'SUCCESS'
          ? platformColors.success
          : platformColors.secondaryText
  return (
    <div style={{ padding: '6px 0', borderBottom: `1px solid ${platformColors.border}`, fontSize: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ color: platformColors.mutedText, fontFamily: 'monospace', fontSize: 11 }}>
          {event.timestamp ? formatDateTime(event.timestamp) : '—'}
        </span>
        <span style={{ color: levelColor, fontWeight: 600, fontSize: 11 }}>{event.level}</span>
        {event.stage && <span style={{ color: platformColors.mutedText, fontSize: 11 }}>{event.stage}</span>}
      </div>
      <div style={{ marginTop: 2, color: platformColors.primaryText }}>{event.message}</div>
      {(event.error_code || event.error_detail) && (
        <div style={{ marginTop: 2, color: platformColors.danger, fontSize: 11 }}>
          {event.error_code ? `错误类型：${event.error_code}` : null}
          {event.error_code && event.error_detail ? ' · ' : null}
          {event.error_detail ? `错误详情：${event.error_detail}` : null}
        </div>
      )}
    </div>
  )
}

/** VistoeraTASK-00413 — 任务执行日志 V1 与完整日志一键复制能力 (任务编号
 * VistoeraTASK-00413-20261004-1125-012). Single UI section inserted after
 * 基本信息/时间线、before Payload/Result (spec §二/§十五). The readable
 * timeline AND the "复制完整日志" clipboard text both come from the SAME
 * `log.full_text` / `log.events` fields the backend's single canonical
 * `TaskExecutionLogSerializer` produced (spec §五) — never a second,
 * DOM-derived copy implementation. Copying always copies the COMPLETE
 * `full_text` string regardless of whether the panel below is scrolled or
 * the timeline is collapsed (spec §四 item 7/8). */
function TaskExecutionLogSection({ log }: { log: PlatformTaskExecutionLog }) {
  const isFailed = log.status === 'failed'
  const [expanded, setExpanded] = useState(isFailed)
  const statusLabel = isFailed ? '失败' : log.status === 'succeeded' ? '成功' : log.status
  const countLine =
    isFailed && log.failure?.stage
      ? `[${statusLabel}] ${log.events.length} 条事件 · ${log.failure.stage}`
      : `[${statusLabel}] ${log.events.length} 条事件`

  const onCopy = () => copyToClipboard(log.full_text)

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: platformColors.primaryText }}>执行日志</div>
        <Button size="small" onClick={onCopy}>复制完整日志</Button>
      </div>

      <div style={{ fontSize: 12, color: isFailed ? platformColors.danger : platformColors.secondaryText, marginBottom: 6 }}>
        {countLine}
      </div>

      {log.is_legacy && (
        <div style={{ fontSize: 12, color: platformColors.mutedText, marginBottom: 8, fontStyle: 'italic' }}>
          该任务创建于执行日志功能启用之前，详细阶段记录不可用。
        </div>
      )}

      {/* Spec §十六 — a failed task's Failure Stage/Error Code/Error Detail
       * must be prominently visible at the TOP, before the admin has to
       * scroll the timeline to find it. */}
      {isFailed && log.failure && (
        <div
          style={{
            background: '#FEF2F2',
            border: `1px solid ${platformColors.danger}`,
            borderRadius: 6,
            padding: 10,
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          <div>
            <strong>失败阶段：</strong>
            {log.failure.stage ?? '—'}
          </div>
          <div>
            <strong>错误类型：</strong>
            {log.failure.error_code ?? '—'}
          </div>
          <div>
            <strong>错误详情：</strong>
            {log.failure.error_detail ?? '—'}
          </div>
        </div>
      )}

      {/* Spec §十六 — failed tasks default EXPANDED, successful tasks
       * default collapsed/brief. */}
      <div
        style={{ cursor: 'pointer', fontSize: 12, color: platformColors.primary, marginBottom: 6 }}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? '收起事件时间线 ▲' : '展开事件时间线（共 ' + log.events.length + ' 条） ▼'}
      </div>

      {expanded && (
        <div style={{ marginBottom: 10 }}>
          {log.events.map((e, idx) => (
            <ExecutionEventRow key={idx} event={e} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: platformColors.primaryText }}>完整日志</span>
        <Button size="small" onClick={onCopy}>复制完整日志</Button>
      </div>
      {/* Readonly, monospace, internally-scrollable (bounded height so the
       * Drawer itself never grows unbounded — spec §十五). Text remains
       * selectable/copyable manually too, but the button above is the
       * primary, guaranteed-complete copy path (never relies on the user
       * selecting the full scrolled contents by hand). */}
      <pre
        style={{
          background: '#0B1220',
          color: '#E2E8F0',
          border: `1px solid ${platformColors.border}`,
          borderRadius: 6,
          padding: 10,
          fontSize: 11,
          lineHeight: 1.5,
          maxHeight: 280,
          overflow: 'auto',
          margin: 0,
          whiteSpace: 'pre-wrap',
          userSelect: 'text',
        }}
      >
        {log.full_text}
      </pre>
    </>
  )
}

interface TaskControlSectionProps {
  workers: AdminWorkerRow[]
  /** VISTOERA_WORKER_CAPABILITY_ASSIGNMENT_OPERATIONAL_FIX_V1_1 item 3 — set
   * by the Overview page's "任务调度异常" banner click-through: jumps this
   * section to `status=queued` + the alert's `job_type` and scrolls it into
   * view. `focusToken` increments on every click so re-clicking the same
   * banner re-applies the filter even if the admin already changed it. */
  focusRequest?: { jobType: string; focusToken: number } | null
}

export default function TaskControlSection({ workers, focusRequest }: TaskControlSectionProps) {
  const { t } = useTranslation(['platformAdmin', 'actions'])
  const navigate = useNavigate()
  const sectionRef = useRef<HTMLDivElement | null>(null)
  // VISTOERA_DEEPZOOM_TASK_OBSERVABILITY_P0 root-cause fix: this used to
  // default to `'queue'`, which force-filters to `status=queued` (see
  // `effectiveFilters` below) — the *first* thing a Platform Admin ever
  // saw on this page was "共 0 条 / 暂无任务数据" the moment nothing
  // happened to be queued at that exact instant, even with real
  // succeeded/running/failed tasks (the Overview's own "任务总量" cards,
  // sourced from the same `jobs` table with no status filter, showed
  // them just above). "队列顺序" is still one click away for anyone who
  // actually wants to reorder the queue — it is just no longer the
  // landing view that decides whether this section looks "empty".
  // VistoeraTASK-00211 — landing view: 开始时间 DESC (server-side), not 关注顺序.
  const [viewMode, setViewMode] = useState<ViewMode>('custom')
  const [customSort, setCustomSort] = useState<TaskControlCustomSort | null>(DEFAULT_TASK_CONTROL_CUSTOM_SORT)
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const loadSeqRef = useRef(0)
  const inFlightRef = useRef(0)

  // VistoeraTASK-00362 — backend-driven canonical "类型"/"执行方" filter
  // metadata (never hardcoded — see module docstring above `EMPTY_FILTER_
  // OPTIONS`). Fetched once; a future new business type/Provider needs
  // zero frontend change to appear here.
  const [filterOptions, setFilterOptions] = useState<PlatformTaskFilterOptionsResponse>(EMPTY_FILTER_OPTIONS)
  useEffect(() => {
    platformGetTaskFilterOptions()
      .then((res) => setFilterOptions(res.data))
      .catch(() => setFilterOptions(EMPTY_FILTER_OPTIONS))
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    if (!focusRequest) return
    setViewMode('custom')
    setCustomSort(null)
    setFilters({ ...EMPTY_FILTERS, status: 'queued', businessType: focusRequest.jobType })
    setSearchInput('')
    setDebouncedSearch('')
    setPage(1)
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest?.focusToken])

  const [data, setData] = useState<PlatformTaskListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [drawerTaskId, setDrawerTaskId] = useState<string | null>(null)
  const [resultTaskId, setResultTaskId] = useState<string | null>(null)
  const [drawerDetail, setDrawerDetail] = useState<PlatformTaskDetail | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [batchDeleting, setBatchDeleting] = useState(false)
  const [pausingId, setPausingId] = useState<string | null>(null)
  const [resumingId, setResumingId] = useState<string | null>(null)
  const [batchPausing, setBatchPausing] = useState(false)
  const [batchResuming, setBatchResuming] = useState(false)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const draggedIdRef = useRef<string | null>(null)
  const [reordering, setReordering] = useState(false)
  const [pinningId, setPinningId] = useState<string | null>(null)
  const [redoingId, setRedoingId] = useState<string | null>(null)

  // Queue view (spec §五十三-§七十二): forces status=queued + clears every
  // other filter, and only shows drag handles while the whole queue fits
  // on one page — see module docstring for why this is required for
  // correctness, not just UX.
  const isQueueView = viewMode === 'queue'
  const { sort, sortDir } = resolveTaskControlListSort(viewMode, customSort)
  const effectivePageSize = isQueueView ? 200 : pageSize
  const effectivePage = isQueueView ? 1 : page
  const effectiveFilters = isQueueView ? { ...EMPTY_FILTERS, status: 'queued' } : filters
  const effectiveSearch = isQueueView ? '' : debouncedSearch

  const load = useCallback(
    (opts?: { silent?: boolean }) => {
      // A background refresh never stacks on a list request that is still
      // running; filter/page changes still go through (stale results are
      // dropped by `loadSeqRef`).
      if (opts?.silent && inFlightRef.current > 0) return
      inFlightRef.current += 1
      const seq = ++loadSeqRef.current
      if (!opts?.silent) setLoading(true)
      platformListTasks({
        page: effectivePage,
        page_size: effectivePageSize,
        status: effectiveFilters.status || undefined,
        business_type: effectiveFilters.businessType || undefined,
        executor: effectiveFilters.executor || undefined,
        search: effectiveSearch || undefined,
        date_from: effectiveFilters.dateFrom || undefined,
        date_to: effectiveFilters.dateTo || undefined,
        sort,
        sort_dir: sortDir,
      })
        .then((res) => {
          if (seq !== loadSeqRef.current) return
          setData(res.data)
          setError(null)
        })
        .catch((err) => {
          if (seq !== loadSeqRef.current) return
          setError(err.message || '任务列表加载失败')
        })
        .finally(() => {
          inFlightRef.current -= 1
          if (seq === loadSeqRef.current) setLoading(false)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectivePage, effectivePageSize, effectiveFilters.status, effectiveFilters.businessType, effectiveFilters.executor, effectiveSearch, effectiveFilters.dateFrom, effectiveFilters.dateTo, sort, sortDir],
  )

  useEffect(() => {
    load()
  }, [load])

  // Real queue reordering never needs a poll to "look" fresh — but a
  // light background refresh keeps the non-queue views reasonably live
  // without disrupting an in-progress drag (skipped while `reordering`).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'hidden') return
      if (!reordering) load({ silent: true })
    }, 15000)
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !reordering) load({ silent: true })
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [load, reordering])

  const updateFilter = (patch: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
    if (isQueueView) setViewMode('custom')
  }

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS)
    setSearchInput('')
    setDebouncedSearch('')
    setPage(1)
  }

  const applyQuickDate = (preset: 'today' | 'yesterday' | '7d' | '30d') => {
    const now = dayjs()
    let from = now.startOf('day')
    let to = now.endOf('day')
    if (preset === 'yesterday') {
      from = now.subtract(1, 'day').startOf('day')
      to = now.subtract(1, 'day').endOf('day')
    } else if (preset === '7d') {
      from = now.subtract(6, 'day').startOf('day')
    } else if (preset === '30d') {
      from = now.subtract(29, 'day').startOf('day')
    }
    updateFilter({ dateFrom: from.toISOString(), dateTo: to.toISOString() })
  }

  const loadDrawerDetail = useCallback((taskId: string) => {
    setDrawerLoading(true)
    platformGetTaskDetail(taskId)
      .then((res) => setDrawerDetail(res.data))
      .catch((err) => message.error(err.message || '加载任务详情失败'))
      .finally(() => setDrawerLoading(false))
  }, [])

  useEffect(() => {
    if (drawerTaskId) loadDrawerDetail(drawerTaskId)
    else setDrawerDetail(null)
  }, [drawerTaskId, loadDrawerDetail])

  // VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §5/§24 — confirmation UI is the
  // anchored `VistoeraActionConfirm` (two trigger sites below: row action
  // column and detail Drawer). Rethrow on failure so the popover stays open
  // with the error toast instead of silently closing on a failed mutation.
  const doDelete = async (taskId: string) => {
    setDeletingId(taskId)
    try {
      const res = await platformDeleteTask(taskId)
      message.success(res.data.mode === 'hard_delete' ? '任务已删除' : '任务已终止（转为失败状态）')
      setSelectedKeys((prev) => prev.filter((k) => k !== taskId))
      if (drawerTaskId === taskId) setDrawerTaskId(null)
      load({ silent: true })
    } catch (err) {
      message.error((err as Error).message || '删除失败')
      throw err
    } finally {
      setDeletingId(null)
    }
  }

  const doBatchDelete = () => {
    Modal.confirm({
      title: `删除 ${selectedKeys.length} 个任务？`,
      content: '此操作不可恢复。租户不会收到任何专门通知，未完成任务将按现有生命周期显示为“失败”。',
      okText: '删除任务',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setBatchDeleting(true)
        try {
          const res = await platformBatchDeleteTasks(selectedKeys)
          message.success(`已删除 ${res.data.deleted_count} 个任务${res.data.failed_count ? `，${res.data.failed_count} 个失败` : ''}`)
          setSelectedKeys([])
          load({ silent: true })
        } catch (err) {
          message.error((err as Error).message || '批量删除失败')
        } finally {
          setBatchDeleting(false)
        }
      },
    })
  }

  // VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §5 — single-row icon-triggered
  // lightweight confirmation, migrated from centered `Modal.confirm` to the
  // anchored `VistoeraActionConfirm` wrapping the trigger button in the
  // `actions` column render below. Mutation/API contract unchanged.
  const doPause = async (taskId: string) => {
    setPausingId(taskId)
    try {
      await platformPauseTask(taskId)
      message.success('任务已暂停')
      load({ silent: true })
    } catch (err) {
      message.error((err as { message?: string }).message || '暂停失败，任务可能已被 Worker 领取')
      load({ silent: true })
      throw err
    } finally {
      setPausingId(null)
    }
  }

  const doResume = async (taskId: string) => {
    setResumingId(taskId)
    try {
      const res = await platformResumeTask(taskId)
      message.success(res.data.message || '任务已恢复排队')
      load({ silent: true })
    } catch (err) {
      message.error((err as { message?: string }).message || '恢复失败')
      load({ silent: true })
    } finally {
      setResumingId(null)
    }
  }

  const doBatchPause = () => {
    Modal.confirm({
      title: `暂停 ${selectedKeys.length} 个任务？`,
      content: '仅排队中的任务会被暂停；已开始执行或不可暂停的任务将被跳过。',
      okText: '批量暂停',
      cancelText: '取消',
      onOk: async () => {
        setBatchPausing(true)
        try {
          const res = await platformBatchPauseTasks(selectedKeys)
          message.success(`已暂停 ${res.data.paused_count} 个任务${res.data.skipped_count ? `，跳过 ${res.data.skipped_count} 个` : ''}`)
          setSelectedKeys([])
          load({ silent: true })
        } catch (err) {
          message.error((err as Error).message || '批量暂停失败')
        } finally {
          setBatchPausing(false)
        }
      },
    })
  }

  const doBatchResume = () => {
    Modal.confirm({
      title: `恢复 ${selectedKeys.length} 个任务？`,
      content: '仅已暂停的任务会被恢复；其他状态的任务将被跳过。',
      okText: '批量恢复',
      cancelText: '取消',
      onOk: async () => {
        setBatchResuming(true)
        try {
          const res = await platformBatchResumeTasks(selectedKeys)
          message.success(`已恢复 ${res.data.resumed_count} 个任务${res.data.skipped_count ? `，跳过 ${res.data.skipped_count} 个` : ''}`)
          setSelectedKeys([])
          load({ silent: true })
        } catch (err) {
          message.error((err as Error).message || '批量恢复失败')
        } finally {
          setBatchResuming(false)
        }
      },
    })
  }

  const queueRows = data?.items ?? []
  const queueFitsOnePage = isQueueView && (data?.total ?? 0) <= effectivePageSize
  const dragEnabled = isQueueView && queueFitsOnePage

  const onDropRow = async (targetTaskId: string) => {
    const draggedId = draggedIdRef.current
    draggedIdRef.current = null
    setDragOverId(null)
    if (!draggedId || draggedId === targetTaskId) return
    const targetIndex = queueRows.findIndex((r) => r.task_id === targetTaskId)
    if (targetIndex < 0) return
    setReordering(true)
    try {
      await platformReorderTask(draggedId, targetIndex)
      message.success('顺序已更新')
      load({ silent: true })
    } catch (err) {
      message.error((err as Error).message || '调整队列顺序失败，可能任务已开始执行')
      load({ silent: true })
    } finally {
      setReordering(false)
    }
  }

  const doPin = async (taskId: string) => {
    setPinningId(taskId)
    try {
      const res = await platformPinTask(taskId)
      message.success(res.data.message || `已将任务 ${taskId.slice(0, 8)} 调整为排队第 1 位`)
      load({ silent: true })
    } catch (err) {
      const apiErr = err as { message?: string; code?: string }
      message.error(apiErr.message || '置顶失败，任务可能已开始执行')
      load({ silent: true })
    } finally {
      setPinningId(null)
    }
  }

  const doRedo = async (taskId: string) => {
    setRedoingId(taskId)
    try {
      const res = await platformRedoTask(taskId)
      message.success(res.data.message || `已创建重做任务 ${res.data.new_task_id.slice(0, 8)}`)
      load({ silent: true })
    } catch (err) {
      message.error((err as Error).message || '重做失败')
    } finally {
      setRedoingId(null)
    }
  }

  // VISTOERA_TASK_FAILURE_DIAGNOSTICS_WORKER_ELIGIBILITY_AND_ADMIN_
  // RECOVERY_GUIDANCE_V1 (spec §13/§26) — reason_code-driven recommended
  // actions, dispatched to already-existing mutations (`doRedo`/
  // `platformSetWorkerDesiredState`) or cross-page navigation only. Never
  // an automatic retry loop (spec §26: "禁止自动重新提交") — every action
  // here is a single explicit admin click.
  const [diagnosticActionBusy, setDiagnosticActionBusy] = useState<string | null>(null)
  const runDiagnosticAction = async (action: TaskDiagnosticAction, detail: PlatformTaskDetail) => {
    const failureWorkerId =
      detail.diagnosis?.worker_state_at_failure?.worker_id ??
      detail.diagnosis?.worker_state_now?.worker_id ??
      detail.diagnosis?.eligibility?.ineligible_workers?.[0]?.worker_id ??
      detail.worker.worker_id ??
      null
    switch (action.kind) {
      case 'retry_task':
        await doRedo(detail.task_id)
        return
      case 'view_worker':
      case 'view_worker_config':
        if (failureWorkerId) navigate(`/platform/workers?worker=${encodeURIComponent(failureWorkerId)}`)
        else navigate('/platform/workers')
        return
      case 'view_capability_workers':
        navigate('/platform/workers')
        return
      case 'open_credentials':
      case 'test_credential':
        navigate('/platform/provider-credentials')
        return
      case 'set_online':
      case 'set_auto':
        if (!failureWorkerId) {
          message.warning('未能确定目标 Worker')
          return
        }
        setDiagnosticActionBusy(action.kind)
        try {
          await platformSetWorkerDesiredState(failureWorkerId, action.kind === 'set_online' ? 'ONLINE' : 'AUTO')
          message.success('已更新 Worker 期望状态')
          if (drawerTaskId) loadDrawerDetail(drawerTaskId)
        } catch (err) {
          message.error((err as Error).message || '设置失败')
        } finally {
          setDiagnosticActionBusy(null)
        }
        return
      default:
        return
    }
  }

  // VistoeraTASK-00362 — "类型" options: backend canonical
  // CANONICAL_TASK_TYPE_MATRIX (business types, never raw job_type).
  const typeOptions = useMemo(
    () => [{ value: '', label: '全部类型' }, ...filterOptions.task_types],
    [filterOptions.task_types],
  )

  // "执行方" options: Worker Registry (existing `workers` prop — already
  // includes offline/historical Workers, task spec §十) UNIONED with the
  // backend-derived API Provider executors (`filterOptions.api_executors`)
  // — WORKER and API_PROVIDER as co-equal first-class citizens in the same
  // dropdown, each carrying its own canonical `executor_id` value
  // (`worker:<id>` / `api:<provider>:<model>`) so the combined list never
  // collides and the backend filter can parse either shape directly.
  const executorOptions = useMemo(
    () => [
      { value: '', label: '全部执行方' },
      ...workers.map((w) => ({ value: `worker:${w.worker_id}`, label: w.name || w.worker_id })),
      ...filterOptions.api_executors,
    ],
    [workers, filterOptions.api_executors],
  )

  const columns: TableColumnsType<PlatformTaskRow> = [
    ...(dragEnabled
      ? [
          {
            title: '',
            key: 'drag_handle',
            width: 32,
            render: (_: unknown, row: PlatformTaskRow) =>
              row.reorderable ? (
                <HolderOutlined style={{ cursor: 'grab', color: platformColors.mutedText }} />
              ) : null,
          },
        ]
      : []),
    { title: '任务ID', key: 'task_id', width: 110, render: (_: unknown, row: PlatformTaskRow) => <TaskIdCell taskId={row.task_id} /> },
    {
      title: '类型',
      key: 'business_type_label',
      width: 96,
      // VistoeraTASK-00362 — canonical BUSINESS_TASK_TYPE label; falls
      // back to the legacy `job_type_label` only for an already-cached/
      // older response shape that predates this field (never crashes).
      render: (_: unknown, row: PlatformTaskRow) => row.business_type_label ?? row.job_type_label,
    },
    {
      title: '租户',
      dataIndex: 'tenant_account',
      width: 180,
      render: (_v: string | null, row: PlatformTaskRow) => <TenantCell row={row} />,
    },
    {
      title: '提示词/摘要',
      dataIndex: 'prompt_summary',
      width: 220,
      // P0-B spec §6: text-heavy column opts back to left.
      align: 'left' as const,
      render: (v: string | null) => (
        <Tooltip title={v ?? undefined}>
          <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v ?? '—'}</div>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      key: 'status',
      dataIndex: 'status',
      width: 96,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: !isQueueView && customSort?.field === 'status' ? (customSort.dir === 'desc' ? 'descend' : 'ascend') : null,
      render: (status: string, row: PlatformTaskRow) => (
        <div>
          <Tooltip
            title={
              // VISTOERA_WORKER_CAPABILITY_ASSIGNMENT_OPERATIONAL_FIX_V1_1
              // item 3 — a real "调度状态" reason instead of a bare "排队中"
              // whenever this queued task currently has zero eligible Worker.
              row.schedule_status && row.schedule_status.reason_code !== 'eligible' ? row.schedule_status.reason_label : undefined
            }
          >
            <span>
              <StatusBadge
                status={status}
                label={
                  row.schedule_status && row.schedule_status.reason_code !== 'eligible'
                    ? row.schedule_status.reason_label
                    : row.status_label
                }
              />
            </span>
          </Tooltip>
          <DiagnosticSummaryLine row={row} />
        </div>
      ),
    },
    { title: 'Worker / 模型', key: 'worker', width: 168, render: (_: unknown, row: PlatformTaskRow) => <WorkerCell worker={row.worker} row={row} /> },
    {
      title: '开始时间',
      key: 'started_at',
      dataIndex: 'started_at',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: !isQueueView && customSort?.field === 'started_at' ? (customSort.dir === 'desc' ? 'descend' : 'ascend') : null,
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: '结束时间',
      key: 'finished_at',
      dataIndex: 'ended_at',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: !isQueueView && customSort?.field === 'finished_at' ? (customSort.dir === 'desc' ? 'descend' : 'ascend') : null,
      render: (v: string | null) => formatDateTime(v),
    },
    { title: '总耗时', dataIndex: 'duration_label', width: 110 },
    {
      title: '创建时间',
      key: 'created_at',
      dataIndex: 'created_at',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: !isQueueView && (customSort?.field === 'created_at' || (!customSort && (viewMode === 'newest' || viewMode === 'oldest'))) ? (sortDir === 'desc' ? 'descend' : 'ascend') : null,
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: '操作',
      key: 'actions',
      width: 228,
      fixed: 'right',
      render: (_: unknown, row: PlatformTaskRow) => (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
          <Tooltip title={row.viewable ? '查看结果' : '暂无可查看结果'}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              disabled={!row.viewable}
              onClick={() => setResultTaskId(row.task_id)}
            />
          </Tooltip>
          <Tooltip title="查看详情">
            <Button size="small" icon={<ProfileOutlined />} onClick={() => setDrawerTaskId(row.task_id)} />
          </Tooltip>
          {row.job_type === 'video_generation' && row.status === 'succeeded' && row.viewable && (
            <Tooltip title="下载结果">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                href={platformTaskMediaDownloadUrl(row.task_id, 'output')}
                download={`vistoera-video-${row.task_id.slice(0, 8)}.mp4`}
              />
            </Tooltip>
          )}
          {row.pausable && (
            <VistoeraActionConfirm
              tone="default"
              tooltip={t('platformAdmin:taskControl.pause.tooltip')}
              title={t('platformAdmin:taskControl.pause.confirmTitle')}
              description={t('platformAdmin:taskControl.pause.confirmDescription')}
              confirmLabel={t('platformAdmin:taskControl.pause.confirmLabel')}
              cancelLabel={t('actions:cancel')}
              onConfirm={() => doPause(row.task_id)}
            >
              <Button size="small" icon={<PauseOutlined />} loading={pausingId === row.task_id} />
            </VistoeraActionConfirm>
          )}
          {!row.pausable && !row.resumable && row.status === 'running' && row.job_type === 'video_generation' && (
            <Tooltip title="任务已开始执行，当前视频处理器不支持断点暂停">
              <Button size="small" icon={<PauseOutlined />} disabled />
            </Tooltip>
          )}
          {row.resumable && (
            <Tooltip title="恢复任务">
              <Button
                size="small"
                icon={<CaretRightOutlined />}
                loading={resumingId === row.task_id}
                onClick={() => doResume(row.task_id)}
              />
            </Tooltip>
          )}
          {row.reorderable && (
            <Tooltip title="置顶">
              <Button
                size="small"
                icon={<VerticalAlignTopOutlined />}
                loading={pinningId === row.task_id}
                onClick={() => doPin(row.task_id)}
              />
            </Tooltip>
          )}
          {row.redoable && (
            <Tooltip title="重做">
              <Button
                size="small"
                icon={<RedoOutlined />}
                loading={redoingId === row.task_id}
                onClick={() => doRedo(row.task_id)}
              />
            </Tooltip>
          )}
          <VistoeraActionConfirm
            tooltip={t('platformAdmin:taskControl.delete.tooltip')}
            title={t('platformAdmin:taskControl.delete.confirmTitle')}
            description={t('platformAdmin:taskControl.delete.confirmDescriptionWithId', { taskId: row.task_id.slice(0, 8) })}
            confirmLabel={t('platformAdmin:taskControl.delete.confirmLabel')}
            cancelLabel={t('actions:cancel')}
            onConfirm={() => doDelete(row.task_id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} loading={deletingId === row.task_id} />
          </VistoeraActionConfirm>
        </div>
      ),
    },
  ]

  const onTableChange: TableProps<PlatformTaskRow>['onChange'] = (pagination, _filters, sorter) => {
    if (!isQueueView) {
      if (pagination.current) setPage(pagination.current)
      if (pagination.pageSize) {
        setPageSize(pagination.pageSize)
        setPage(1)
      }
    }
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    const sortField = s?.columnKey ?? s?.field
    if (s?.order && sortField) {
      setViewMode('custom')
      setCustomSort({ field: String(sortField), dir: s.order === 'descend' ? 'desc' : 'asc' })
      setPage(1)
    }
  }

  return (
    <div ref={sectionRef}>
    <SectionCard
      title={
        <span>
          任务控制中心
          <span style={{ fontSize: 12, color: platformColors.mutedText, fontWeight: 400, marginLeft: 8 }}>
            共 {data?.total ?? 0} 条
          </span>
        </span>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={() => load()}>
          刷新
        </Button>
      }
    >
      {error && <div style={{ color: platformColors.danger, fontSize: 12, marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        {(['attention', 'queue', 'newest', 'oldest'] as ViewMode[]).map((mode) => (
          <Button
            key={mode}
            size="small"
            type={viewMode === mode ? 'primary' : 'default'}
            onClick={() => {
              setViewMode(mode)
              setCustomSort(null)
              setPage(1)
            }}
          >
            {mode === 'attention'
              ? '关注顺序'
              : mode === 'queue'
                ? '队列顺序'
                : mode === 'newest'
                  ? '最新创建'
                  : '最早创建'}
          </Button>
        ))}
        {isQueueView && (
          <span style={{ fontSize: 12, color: platformColors.mutedText, alignSelf: 'center', marginLeft: 4 }}>
            {queueFitsOnePage
              ? '拖动 ⋮⋮ 调整排队中任务的真实执行顺序（不影响 running/已完成任务）'
              : `队列长度（${data?.total ?? 0}）超过单页展示上限（${effectivePageSize}），暂不支持拖拽`}
          </span>
        )}
        {!isQueueView && (
          <span style={{ fontSize: 12, color: platformColors.mutedText, alignSelf: 'center', marginLeft: 4 }}>
            {viewMode === 'attention'
              ? '关注顺序：运行中 → 排队中 → 终态；仅改变展示，不影响真实执行顺序'
              : '当前为查看排序，不影响真实执行顺序；切换至"队列顺序"后可调整任务优先级'}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 6, alignItems: 'center', opacity: isQueueView ? 0.5 : 1 }}>
        <RangePicker
          size="small"
          disabled={isQueueView}
          value={filters.dateFrom && filters.dateTo ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)] : null}
          onChange={(vals) => {
            if (!vals || !vals[0] || !vals[1]) {
              updateFilter({ dateFrom: null, dateTo: null })
              return
            }
            updateFilter({
              dateFrom: vals[0].startOf('day').toISOString(),
              dateTo: vals[1].endOf('day').toISOString(),
            })
          }}
        />
        <Select size="small" style={{ width: 88 }} placeholder="快捷日期" disabled={isQueueView} onChange={(v) => applyQuickDate(v)} options={[
          { value: 'today', label: '今天' },
          { value: 'yesterday', label: '昨天' },
          { value: '7d', label: '最近7天' },
          { value: '30d', label: '最近30天' },
        ]} />
        <Select
          size="small"
          style={{ width: 110 }}
          disabled={isQueueView}
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={(v) => updateFilter({ status: v })}
        />
        <Select
          size="small"
          style={{ width: 140 }}
          disabled={isQueueView}
          value={filters.businessType}
          options={typeOptions}
          onChange={(v) => updateFilter({ businessType: v })}
        />
        <Select
          size="small"
          style={{ width: 180 }}
          disabled={isQueueView}
          value={filters.executor}
          options={executorOptions}
          onChange={(v) => updateFilter({ executor: v })}
        />
        <Input
          size="small"
          style={{ width: 220 }}
          placeholder="搜索 Task ID / 提示词 / 租户"
          disabled={isQueueView}
          prefix={<SearchOutlined />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          onClear={() => setSearchInput('')}
        />
        <Button size="small" disabled={isQueueView} onClick={resetFilters}>
          重置
        </Button>
      </div>

      {selectedKeys.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: platformColors.infoBg,
            border: `1px solid ${platformColors.info}`,
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 10,
            fontSize: 13,
          }}
        >
          <span>已选择 {selectedKeys.length} 项</span>
          <Button size="small" loading={batchPausing} onClick={doBatchPause}>
            批量暂停
          </Button>
          <Button size="small" loading={batchResuming} onClick={doBatchResume}>
            批量恢复
          </Button>
          <Button size="small" danger loading={batchDeleting} onClick={doBatchDelete}>
            删除任务
          </Button>
          <Button size="small" onClick={() => setSelectedKeys([])}>
            取消选择
          </Button>
        </div>
      )}

      <Table<PlatformTaskRow>
        className="pf-data-table"
        rowKey="task_id"
        loading={loading}
        dataSource={queueRows}
        columns={columns}
        scroll={{ x: 1200 }}
        sticky
        size="small"
        locale={{ emptyText: '暂无任务数据' }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          preserveSelectedRowKeys: true,
          onChange: (keys) => setSelectedKeys(keys as string[]),
        }}
        pagination={
          isQueueView
            ? false
            : {
                current: page,
                pageSize,
                total: data?.total ?? 0,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                showTotal: (t) => `共 ${t} 条`,
              }
        }
        onChange={onTableChange}
        onRow={(row) =>
          dragEnabled && row.reorderable
            ? {
                draggable: true,
                onDragStart: () => {
                  draggedIdRef.current = row.task_id
                },
                onDragOver: (e) => {
                  e.preventDefault()
                  setDragOverId(row.task_id)
                },
                onDrop: () => onDropRow(row.task_id),
                onDragEnd: () => {
                  draggedIdRef.current = null
                  setDragOverId(null)
                },
                style: dragOverId === row.task_id ? { background: platformColors.primarySoft } : undefined,
              }
            : {}
        }
      />

      <Drawer title={drawerTaskId ? `任务详情 — ${drawerTaskId.slice(0, 8)}` : '任务详情'} open={!!drawerTaskId} onClose={() => setDrawerTaskId(null)} width={460} loading={drawerLoading}>
        {drawerDetail && (
          <div>
            <DetailRow label="任务ID" value={<Tooltip title={drawerDetail.task_id}>{drawerDetail.task_id}</Tooltip>} />
            <DetailRow label="类型" value={drawerDetail.business_type_label ?? drawerDetail.job_type_label} />
            {drawerDetail.is_admin_test ? (
              <>
                <DetailRow
                  label="Admin Direct Test"
                  value={
                    <span>
                      <AdminTestBadge row={drawerDetail} />
                      {drawerDetail.admin_test_requested_by_email ? ` 发起人：${drawerDetail.admin_test_requested_by_email}` : ''}
                    </span>
                  }
                />
                <DetailRow label="目标 Worker" value={drawerDetail.admin_test_target_worker_id ?? '—'} />
                <DetailRow
                  label="实际领取 Worker"
                  value={
                    drawerDetail.worker?.worker_id
                      ? `${drawerDetail.worker.worker_name || drawerDetail.worker.display || drawerDetail.worker.worker_id} (${drawerDetail.worker.worker_id})`
                      : '尚未领取'
                  }
                />
              </>
            ) : null}
            <DetailRow
              label="状态"
              value={
                <StatusBadge
                  status={drawerDetail.status}
                  label={
                    drawerDetail.schedule_status && drawerDetail.schedule_status.reason_code !== 'eligible'
                      ? drawerDetail.schedule_status.reason_label
                      : drawerDetail.status_label
                  }
                />
              }
            />
            {/* VISTOERA_WORKER_CAPABILITY_ASSIGNMENT_OPERATIONAL_FIX_V1_1
             * item 3 — explicit 调度状态 row, always visible for a queued
             * task (even the healthy "排队中" case), so the reason is never
             * only inferable from a tooltip on the status badge. */}
            {drawerDetail.schedule_status && (
              <DetailRow
                label="调度状态"
                value={`${drawerDetail.schedule_status.reason_label}（当前可用 Worker 数：${drawerDetail.schedule_status.eligible_worker_count}）`}
              />
            )}
            <DetailRow
              label="创建者账号"
              value={
                drawerDetail.created_by_email
                  ? `${drawerDetail.created_by_email}${drawerDetail.created_by_role ? `（${drawerDetail.created_by_role}）` : ''}`
                  : '未记录'
              }
            />
            {drawerDetail.created_by_is_member === false ? (
              <DetailRow
                label="归属告警"
                value={<span style={{ color: platformColors.danger }}>创建者不是该 Workspace 的有效成员</span>}
              />
            ) : null}
            <DetailRow label="Workspace Owner" value={drawerDetail.tenant_account ?? '—'} />
            <DetailRow label="Workspace 名称" value={drawerDetail.tenant_name ?? '—'} />
            {drawerDetail.tenant_id ? <DetailRow label="Workspace ID" value={drawerDetail.tenant_id} /> : null}
            {drawerDetail.site_id && <DetailRow label="Site" value={drawerDetail.site_id} />}
            <DetailRow label="Worker / 模型" value={<WorkerCell worker={drawerDetail.worker} row={drawerDetail} />} />
            {drawerDetail.queue_position != null && <DetailRow label="Queue 位置" value={`#${drawerDetail.queue_position}`} />}

            <DetailSectionTitle>任务参数</DetailSectionTitle>
            <TaskParamsRenderer detail={drawerDetail} />

            <DetailSectionTitle>时间线</DetailSectionTitle>
            <DetailRow label="创建时间" value={formatDateTime(drawerDetail.timeline.created_at)} />
            <DetailRow label="分配时间" value={formatDateTime(drawerDetail.timeline.assigned_at)} />
            <DetailRow label="开始时间" value={formatDateTime(drawerDetail.timeline.started_at)} />
            <DetailRow label="结束时间" value={formatDateTime(drawerDetail.timeline.ended_at)} />
            <DetailRow label="总耗时" value={drawerDetail.duration_label} />
            {drawerDetail.status === 'running' && (
              <>
                <DetailRow label="当前 Stage" value={drawerDetail.stage ?? '—'} />
                <DetailRow label="最后心跳" value={formatDateTime(drawerDetail.timeline.last_heartbeat_at)} />
                {drawerDetail.progress != null && <DetailRow label="进度" value={`${drawerDetail.progress}%`} />}
              </>
            )}

            {drawerDetail.diagnosis && (
              <TaskDiagnosisSection
                diagnosis={drawerDetail.diagnosis}
                actionBusy={diagnosticActionBusy}
                onAction={(action) => runDiagnosticAction(action, drawerDetail)}
              />
            )}

            {drawerDetail.error && (
              <>
                <DetailSectionTitle>失败信息（内部）</DetailSectionTitle>
                {/* Vistoera Video Queue / Premature Failure P0 (spec item 九)
                    — `Job.stage` is never cleared by `fail_remote`'s terminal
                    branch (app/services/jobs.py), so for a `failed` task this
                    is the real last stage it reached before failing (e.g. a
                    Job that failed 5s after claim, before ever reaching
                    `denoise`, shows that here — not hidden behind the
                    `status === 'running'` guard above, which only covers the
                    in-progress view). */}
                <DetailRow label="失败阶段（Failure Stage）" value={drawerDetail.stage ?? '—'} />
                <DetailRow label="Error Code" value={drawerDetail.error.error_code ?? '—'} />
                <DetailRow label="Error Detail" value={drawerDetail.error.error_detail ?? '—'} />
                <DetailRow label="Attempt" value={drawerDetail.runtime.attempt_count ?? '—'} />
              </>
            )}

            {/* VistoeraTASK-00413 — 任务执行日志 V1与完整日志一键复制能力.
             * Inserted after 基本信息/时间线/失败信息, BEFORE Payload/Result
             * (spec §二/§十五) — those two sections below are unchanged. */}
            {drawerDetail.execution_log && <TaskExecutionLogSection log={drawerDetail.execution_log} />}

            <DetailSectionTitle>内部运行信息（仅 Platform Admin 可见）</DetailSectionTitle>
            <DetailRow label="Worker ID" value={drawerDetail.runtime.worker?.worker_id ?? '—'} />
            <DetailRow label="Lease 到期" value={formatDateTime(drawerDetail.runtime.lease_expires_at)} />
            <DetailRow label="Attempt" value={drawerDetail.runtime.attempt_count ?? '—'} />
            {drawerDetail.runtime.worker?.resource_snapshot && (
              <RawJsonBlock label="Worker 资源快照" value={drawerDetail.runtime.worker.resource_snapshot} />
            )}
            <RawJsonBlock label="Payload（脱敏参数）" value={drawerDetail.redacted_payload ?? drawerDetail.payload} />
            <RawJsonBlock label="Result（脱敏结果）" value={drawerDetail.redacted_result ?? drawerDetail.result} />

            <div style={{ marginTop: 20 }}>
              <VistoeraActionConfirm
                title={t('platformAdmin:taskControl.delete.confirmTitle')}
                description={t('platformAdmin:taskControl.delete.confirmDescriptionWithId', { taskId: drawerDetail.task_id.slice(0, 8) })}
                confirmLabel={t('platformAdmin:taskControl.delete.confirmLabel')}
                cancelLabel={t('actions:cancel')}
                onConfirm={() => doDelete(drawerDetail.task_id)}
              >
                <Button danger icon={<DeleteOutlined />} loading={deletingId === drawerDetail.task_id}>
                  {t('platformAdmin:taskControl.delete.confirmLabel')}
                </Button>
              </VistoeraActionConfirm>
            </div>
          </div>
        )}
      </Drawer>
      <TaskResultModal taskId={resultTaskId} onClose={() => setResultTaskId(null)} />
    </SectionCard>
    </div>
  )
}
